import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag,
  Plus,
  Trash,
  CheckCircle,
  Warning,
  ArrowRight,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";

const ORG_TYPES = [
  { value: "hospice", label: "Hospice" },
  { value: "hospital_healthcare", label: "Hospital / Healthcare" },
  { value: "senior_living", label: "Senior Living / Assisted Living" },
  { value: "board_and_care", label: "Board & Care" },
  { value: "church_faith", label: "Church / Faith Community" },
  { value: "business_employer", label: "Business / Employer" },
  { value: "community_organization", label: "Community Organization" },
  { value: "school_education", label: "School / Educational Organization" },
  { value: "individual", label: "Individual" },
  { value: "other", label: "Other" },
];

const PRODUCT_OPTIONS = [
  "From Diagnosis to Goodbye",
  "From Diagnosis to Goodbye Hospice Workbook",
  "From Diagnosis to Goodbye Bereavement Workbook",
  "Other (specify in notes)",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR","VI","GU","AS","MP",
];

const emptyLine = (chosen = new Set()) => {
  // Pick the first product not already selected on another line so the new
  // row starts on a legal, non-duplicate value.
  const first = PRODUCT_OPTIONS.find((p) => !chosen.has(p)) || PRODUCT_OPTIONS[0];
  return { product: first, quantity: 1 };
};

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-semibold text-ink-800 mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-ink-200 px-4 py-2.5 bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all";

const BulkOrderRequestForm = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    organization_name: "",
    organization_type: "",
    email: "",
    phone: "",
    preferred_contact: "email",
    intended_use: "",
    delivery_timeframe: "",
    city: "",
    state: "",
    zip_code: "",
    message: "",
    consent: true,
  });
  const [products, setProducts] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const updateLine = (idx, patch) =>
    setProducts((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    );
  const addLine = () =>
    setProducts((prev) => {
      if (prev.length >= 20) return prev;
      const chosen = new Set(prev.map((p) => p.product));
      // Don't allow a new line if every product is already selected.
      if (chosen.size >= PRODUCT_OPTIONS.length) return prev;
      return [...prev, emptyLine(chosen)];
    });
  const removeLine = (idx) =>
    setProducts((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));

  const selectedProducts = useMemo(
    () => new Set(products.map((p) => p.product)),
    [products]
  );
  // Once every product option has been used, "Add another product" is disabled.
  const canAddMore =
    products.length < 20 && selectedProducts.size < PRODUCT_OPTIONS.length;

  const totalQty = useMemo(
    () => products.reduce((s, p) => s + (Number(p.quantity) || 0), 0),
    [products]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    // Client-side friendly checks; backend validates too.
    if (
      !form.first_name ||
      !form.last_name ||
      !form.organization_name ||
      !form.organization_type ||
      !form.email ||
      !form.city ||
      !form.state ||
      !form.zip_code
    ) {
      toast.error("Please fill in the required fields.");
      return;
    }
    // Defensive: even though the UI disables already-selected products, merge
    // any duplicates by summing quantities so the request is clean before it
    // reaches the backend.
    const clampQty = (n) =>
      Math.max(1, Math.min(100_000, Math.floor(Number(n) || 0)));
    const merged = new Map();
    for (const p of products) {
      const product = (p.product || "").trim();
      if (!product) continue;
      const qty = clampQty(p.quantity);
      merged.set(product, (merged.get(product) || 0) + qty);
    }
    const cleanProducts = Array.from(merged, ([product, quantity]) => ({
      product,
      quantity: Math.min(100_000, quantity),
    }));
    if (cleanProducts.length === 0) {
      toast.error("Please add at least one product.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/bulk-orders`,
        {
          ...form,
          products: cleanProducts,
          source_page:
            typeof window !== "undefined" ? window.location.href : null,
        },
        { withCredentials: false }
      );
      setSubmitted(true);
      toast.success("Bulk Order Quote Request received.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "We couldn't send your request right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form fields.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="bulk-form-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 sm:p-10 text-center"
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white">
          <CheckCircle size={26} weight="fill" />
        </span>
        <h3 className="mt-5 font-heading text-2xl sm:text-3xl text-ink-900 font-medium">
          Thank you — your request has been received.
        </h3>
        <p className="mt-4 text-ink-700 text-base leading-relaxed max-w-xl mx-auto">
          A confirmation email is on its way. Begin Again Living Ministries
          will follow up regarding pricing, availability, shipping, and next
          steps. This is a quote inquiry — no order has been placed yet and no
          payment has been collected.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="bulk-order-form"
      className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 sm:p-10 space-y-8"
      noValidate
    >
      {/* Contact */}
      <fieldset className="space-y-5">
        <legend className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          Contact information
        </legend>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel required>First name</FieldLabel>
            <input
              type="text"
              required
              value={form.first_name}
              onChange={setField("first_name")}
              autoComplete="given-name"
              data-testid="bulk-input-first-name"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel required>Last name</FieldLabel>
            <input
              type="text"
              required
              value={form.last_name}
              onChange={setField("last_name")}
              autoComplete="family-name"
              data-testid="bulk-input-last-name"
              className={inputCls}
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel required>Organization name</FieldLabel>
            <input
              type="text"
              required
              value={form.organization_name}
              onChange={setField("organization_name")}
              autoComplete="organization"
              data-testid="bulk-input-org-name"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel required>Organization type</FieldLabel>
            <select
              required
              value={form.organization_type}
              onChange={setField("organization_type")}
              data-testid="bulk-input-org-type"
              className={inputCls}
            >
              <option value="">Select…</option>
              {ORG_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel required>Email</FieldLabel>
            <input
              type="email"
              required
              value={form.email}
              onChange={setField("email")}
              autoComplete="email"
              data-testid="bulk-input-email"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <input
              type="tel"
              value={form.phone}
              onChange={setField("phone")}
              autoComplete="tel"
              data-testid="bulk-input-phone"
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <FieldLabel>Preferred contact method</FieldLabel>
          <div className="flex flex-wrap gap-4 text-sm">
            {[
              { v: "email", l: "Email" },
              { v: "phone", l: "Phone" },
              { v: "either", l: "Either email or phone" },
            ].map((o) => (
              <label key={o.v} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="preferred_contact"
                  value={o.v}
                  checked={form.preferred_contact === o.v}
                  onChange={setField("preferred_contact")}
                  data-testid={`bulk-input-pref-${o.v}`}
                  className="accent-primary"
                />
                <span>{o.l}</span>
              </label>
            ))}
          </div>
        </div>
      </fieldset>

      {/* Order */}
      <fieldset className="space-y-5 border-t border-ink-100 pt-8">
        <legend className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          Order information
        </legend>
        <div className="space-y-3">
          <FieldLabel required>Products & quantities</FieldLabel>
          {products.map((line, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_100px_36px] gap-2 items-start"
              data-testid={`bulk-line-${idx}`}
            >
              <select
                value={line.product}
                onChange={(e) => updateLine(idx, { product: e.target.value })}
                data-testid={`bulk-line-product-${idx}`}
                className={inputCls}
              >
                {PRODUCT_OPTIONS.map((p) => (
                  <option
                    key={p}
                    value={p}
                    disabled={p !== line.product && selectedProducts.has(p)}
                  >
                    {p}
                    {p !== line.product && selectedProducts.has(p)
                      ? " (already added)"
                      : ""}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                max={100000}
                value={line.quantity}
                onChange={(e) =>
                  updateLine(idx, { quantity: e.target.value })
                }
                data-testid={`bulk-line-qty-${idx}`}
                aria-label="Quantity"
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => removeLine(idx)}
                disabled={products.length <= 1}
                data-testid={`bulk-line-remove-${idx}`}
                aria-label="Remove product line"
                className="inline-flex items-center justify-center h-[42px] w-9 rounded-xl text-ink-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={addLine}
              disabled={!canAddMore}
              data-testid="bulk-line-add"
              className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold hover:text-primary-dark disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} weight="bold" />
              Add another product
            </button>
            <div className="text-xs text-ink-500">
              Total quantity:{" "}
              <span
                className="font-semibold text-ink-800"
                data-testid="bulk-total-qty"
              >
                {totalQty}
              </span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel>Intended use / audience (optional)</FieldLabel>
            <input
              type="text"
              value={form.intended_use}
              onChange={setField("intended_use")}
              placeholder="e.g. Church small-group study, hospice staff training"
              data-testid="bulk-input-intended-use"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel>Requested delivery timeframe (optional)</FieldLabel>
            <input
              type="text"
              value={form.delivery_timeframe}
              onChange={setField("delivery_timeframe")}
              placeholder="e.g. Within 4-6 weeks"
              data-testid="bulk-input-timeframe"
              className={inputCls}
            />
          </div>
        </div>
      </fieldset>

      {/* Location */}
      <fieldset className="space-y-5 border-t border-ink-100 pt-8">
        <legend className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          Shipping location
        </legend>
        <p className="text-xs text-ink-500 -mt-2">
          City, state, and ZIP only at this stage. We'll ask for a full
          shipping address if you decide to proceed.
        </p>
        <div className="grid sm:grid-cols-[1fr_120px_140px] gap-5">
          <div>
            <FieldLabel required>City</FieldLabel>
            <input
              type="text"
              required
              value={form.city}
              onChange={setField("city")}
              autoComplete="address-level2"
              data-testid="bulk-input-city"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel required>State</FieldLabel>
            <select
              required
              value={form.state}
              onChange={setField("state")}
              autoComplete="address-level1"
              data-testid="bulk-input-state"
              className={inputCls}
            >
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel required>ZIP</FieldLabel>
            <input
              type="text"
              required
              value={form.zip_code}
              onChange={setField("zip_code")}
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
              data-testid="bulk-input-zip"
              className={inputCls}
            />
          </div>
        </div>
      </fieldset>

      {/* Notes */}
      <fieldset className="space-y-4 border-t border-ink-100 pt-8">
        <legend className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          Additional information
        </legend>
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2">
          <Warning
            size={16}
            weight="fill"
            className="text-amber-600 mt-0.5 shrink-0"
          />
          <p className="text-xs text-ink-700 leading-relaxed">
            Please do <strong>not</strong> submit confidential medical,
            patient, employee, or other sensitive personal information in this
            form.
          </p>
        </div>
        <div>
          <FieldLabel>Message / special instructions (optional)</FieldLabel>
          <textarea
            rows={5}
            value={form.message}
            onChange={setField("message")}
            data-testid="bulk-input-message"
            placeholder="Anything else we should know about your order or organization?"
            className={inputCls + " resize-y"}
          />
        </div>
      </fieldset>

      <div className="pt-2">
        <p className="text-[11px] text-ink-500 mb-4">
          This is a quote/request workflow. No order will be placed and no
          payment collected until we speak with you and confirm details.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="bulk-order-submit"
          className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
        >
          <ShoppingBag size={18} weight="duotone" />
          {submitting ? "Sending…" : "Request a Bulk Order Quote"}
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </form>
  );
};

export default BulkOrderRequestForm;
